import { useEffect, useState, useCallback } from 'react';
import {
  initializeSessionTimeout,
  updateSessionActivity,
  isSessionExpired,
  clearSessionData,
  getRemainingSessionTime,
  getFormattedRemainingTime,
  startSessionTimeoutCheck,
  stopSessionTimeoutCheck,
  resetSessionTimeout,
  isSessionAboutToExpire
} from '../utils/sessionTimeout';

/**
 * React hook for session timeout management
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoInitialize - Whether to auto-initialize on mount (default: true)
 * @param {boolean} options.autoUpdate - Whether to auto-update on user interactions (default: true)
 * @param {Function} options.onSessionExpired - Callback when session expires
 * @param {Function} options.onSessionAboutToExpire - Callback when session is about to expire
 * @returns {Object} Session timeout state and methods
 */
export const useSessionTimeout = (options = {}) => {
  const {
    autoInitialize = true,
    autoUpdate = true,
    onSessionExpired,
    onSessionAboutToExpire
  } = options;

  const [isExpired, setIsExpired] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [formattedTime, setFormattedTime] = useState('');
  const [isAboutToExpire, setIsAboutToExpire] = useState(false);

  // Update session activity
  const updateActivity = useCallback(() => {
    if (autoUpdate && !isExpired) {
      updateSessionActivity();
      resetSessionTimeout();
    }
  }, [autoUpdate, isExpired]);

  // Check session status
  const checkSessionStatus = useCallback(() => {
    const expired = isSessionExpired();
    const remaining = getRemainingSessionTime();
    const formatted = getFormattedRemainingTime();
    const aboutToExpire = isSessionAboutToExpire();

    setIsExpired(expired);
    setRemainingTime(remaining);
    setFormattedTime(formatted);
    setIsAboutToExpire(aboutToExpire);

    if (expired && onSessionExpired) {
      onSessionExpired();
    }

    if (aboutToExpire && onSessionAboutToExpire) {
      onSessionAboutToExpire();
    }
  }, [onSessionExpired, onSessionAboutToExpire]);

  // Initialize session timeout
  const initialize = useCallback(() => {
    initializeSessionTimeout();
    checkSessionStatus();
  }, [checkSessionStatus]);

  // Clear session data
  const clearSession = useCallback(() => {
    clearSessionData();
    setIsExpired(true);
    setRemainingTime(0);
    setFormattedTime('Session expired');
    setIsAboutToExpire(false);
  }, []);

  // Set up event listeners and intervals
  useEffect(() => {
    if (autoInitialize) {
      initialize();
    }

    // Listen for session expired events
    const handleSessionExpired = () => {
      setIsExpired(true);
      setRemainingTime(0);
      setFormattedTime('Session expired');
      setIsAboutToExpire(false);
      
      if (onSessionExpired) {
        onSessionExpired();
      }
    };

    window.addEventListener('sessionExpired', handleSessionExpired);

    // Set up periodic status check
    const statusInterval = setInterval(checkSessionStatus, 30000); // Check every 30 seconds

    // Set up activity tracking for common user interactions
    if (autoUpdate) {
      const activityEvents = [
        'mousedown',
        'mousemove',
        'keypress',
        'scroll',
        'touchstart',
        'click'
      ];

      const throttledUpdateActivity = throttle(updateActivity, 30000); // Throttle to once per 30 seconds

      activityEvents.forEach(event => {
        document.addEventListener(event, throttledUpdateActivity, true);
      });

      return () => {
        window.removeEventListener('sessionExpired', handleSessionExpired);
        clearInterval(statusInterval);
        activityEvents.forEach(event => {
          document.removeEventListener(event, throttledUpdateActivity, true);
        });
      };
    }

    return () => {
      window.removeEventListener('sessionExpired', handleSessionExpired);
      clearInterval(statusInterval);
    };
  }, [autoInitialize, autoUpdate, initialize, checkSessionStatus, updateActivity, onSessionExpired]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopSessionTimeoutCheck();
    };
  }, []);

  return {
    isExpired,
    remainingTime,
    formattedTime,
    isAboutToExpire,
    updateActivity,
    checkSessionStatus,
    initialize,
    clearSession,
    resetSessionTimeout
  };
};

// Throttle utility function
function throttle(func, limit) {
  let inThrottle;
  return function() {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

export default useSessionTimeout;
